import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useState, } from "react";
import { Box } from "../Box";
import { Button } from "../Button";
import { Grid } from "../Grid";
import { Select } from "../Select";
import { Skeleton } from "../Skeleton";
import { Stack } from "../Stack";
import { TextField } from "../TextField";
import { Typography } from "../Typography";
import { SlotsProvider } from "../../providers/SlotsProvider";
import { theme } from "../../theme";
import { ColorPickerSwatch } from "./ColorPickerSwatch";
import { ColorPickerTextFields } from "./ColorPickerTextFields";
import { colorToHex, colorToHsl, colorToHsv, colorToRgb } from "./utils";
const ColorPickerSlider = ({ isPicker, left, top, ...props }) => {
    return (_jsx(Box, { ...props, sx: {
            width: 2,
            height: 2,
            borderRadius: "50%",
            border: 2,
            borderColor: (theme) => theme.palette.background.main,
            position: "absolute",
            transform: isPicker ? "translate(-50%, -50%)" : "translateX(-50%)",
            boxShadow: "0 0 2px rgba(0, 0, 0, 0.6)",
            left: left,
            top: top,
        } }));
};
export const colorPickerSlots = {
    applyButton: Button,
    colorValueField: TextField,
    colorFormatSelector: Select,
    styledPointer: ColorPickerSlider,
    swatch: ColorPickerSwatch,
};
/**
 * ## Definition
 * The ColorPicker component is used to set color saturation, hue and opacity. It is also able to select color formats(Hex, Hsl, Hsv and Rgb) and manually change its color values.
 *
 *## Behavior
 * - Click in Saturation area, drag the pointer in Hue slider will change the related values in the textfields.
 * - Drag the pointer in Opacity slider will change the value in the last textfield which is opacity.
 * - ColorFormat options can be opened by clicking the first textfield. Selecting one color format will change the following textfields. The color format options will close when clicks one of the options.
 * - The following color value textfield can be manually changed. Changing the value of these textfield will reset the pointer location in Saturation and Hue slider, as well as the color of Saturation area.
 * - The last opacity textfield is controlled by Opacity slider, and it can not be manually changed
 * - Click "Apply" button will trigger onApply callback function
 */
export const ColorPicker = ({ initialColor = "0d5d36", swatchColors = [
    "#EF4444",
    "#F97316",
    "#FACC15",
    "#4ADE80",
    "#2DD4BF",
    "#3B82F6",
    "#6467F2",
    "#EC4899",
    "#F43F5E",
    "#D946EF",
    "#8B5CF6",
    "#0EA5E9",
    "#10B981",
    "#84CC16",
], hideApplyButton = false, applyButtonText = "Apply", onChange, onApply, topNode, bottomNode, slots, slotProps, ...rest }) => {
    const [Hue, setHue] = useState(undefined);
    const [Alpha, setAlpha] = useState(undefined);
    const [Saturation, setSaturation] = useState(undefined);
    useEffect(() => {
        if (typeof window !== "undefined") {
            import("@uiw/react-color").then((module) => {
                setHue(module.Hue);
                setAlpha(module.Alpha);
                setSaturation(module.Saturation);
            });
        }
    }, []);
    const hsvaValue = colorToHsv(initialColor);
    const convertedColor = {
        hex: colorToHex(initialColor),
        hsv: hsvaValue.s <= 1 && hsvaValue.v <= 1
            ? { ...hsvaValue, s: hsvaValue.s * 100, v: hsvaValue.v * 100 }
            : hsvaValue,
        hsl: colorToHsl(initialColor),
        rgb: colorToRgb(initialColor),
    };
    const [color, setColor] = useState(convertedColor);
    const [hsva, setHsva] = useState(convertedColor.hsv);
    const updateColor = (inputColor) => {
        if (typeof inputColor !== "string" && "v" in inputColor) {
            const inputColorFormats = {
                hex: colorToHex(inputColor),
                hsv: inputColor,
                hsl: colorToHsl(inputColor),
                rgb: colorToRgb(inputColor),
            };
            setColor(inputColorFormats);
            setHsva(inputColor);
            onChange?.(inputColorFormats);
        }
        else {
            const hsvaValue = colorToHsv(inputColor);
            const inputColorFormats = {
                hex: colorToHex(inputColor),
                hsv: { ...hsvaValue, s: hsvaValue.s * 100, v: hsvaValue.v * 100 },
                hsl: colorToHsl(inputColor),
                rgb: colorToRgb(inputColor),
            };
            setColor(inputColorFormats);
            setHsva(inputColorFormats.hsv);
            onChange?.(inputColorFormats);
        }
    };
    return (_jsx(SlotsProvider, { baseSlots: colorPickerSlots, slots: slots, children: ({ applyButton: ApplyButton, styledPointer: StyledPointer, swatch: Swatch, ...remainingSlots }) => (_jsxs(Stack, { ...rest, gap: 2, children: [_jsxs(Grid, { container: true, gap: 2, alignItems: "center", children: [_jsx(Box, { width: 4, height: 4, sx: {
                                borderRadius: (theme) => theme.radius.default,
                                backgroundColor: `#${color.hex}`,
                            } }), _jsx(Grid, { item: true, xs: true, children: _jsxs(Typography, { variant: "body2", children: ["#", color.hex] }) }), topNode] }), _jsxs(Stack, { gap: 4, children: [Saturation ? (_jsx(Saturation, { hsva: hsva, onChange: (newColor) => {
                                updateColor({ ...hsva, ...newColor, a: hsva.a });
                            }, style: {
                                width: "100%",
                                height: "100px",
                                borderRadius: theme.radius.default,
                            }, pointer: (props) => (_jsx(StyledPointer, { ...props, isPicker: true, ...slotProps?.styledPointer })) })) : (_jsx(Skeleton, { height: 25, sx: { borderRadius: (theme) => theme.radius.default } })), Hue ? (_jsx(Hue, { hue: hsva.h, onChange: (newHue) => {
                                updateColor({ ...color.hsv, ...newHue });
                            }, radius: theme.radius.full, height: theme.spacing(3), pointer: (props) => (_jsx(StyledPointer, { ...props, ...slotProps?.styledPointer })) })) : (_jsx(Skeleton, { height: 3, sx: { borderRadius: (theme) => theme.radius.full } })), Alpha ? (_jsx(Alpha, { hsva: hsva, onChange: (newAlpha) => {
                                updateColor({ ...color.hsv, ...newAlpha });
                            }, radius: theme.radius.full, height: theme.spacing(3), pointer: (props) => (_jsx(StyledPointer, { ...props, ...slotProps?.styledPointer })) })) : (_jsx(Skeleton, { height: 3, sx: { borderRadius: (theme) => theme.radius.full } })), _jsx(ColorPickerTextFields, { color: color, updateColor: updateColor, slots: remainingSlots, slotProps: {
                                colorFormatSelector: slotProps?.colorFormatSelector,
                                colorValueField: slotProps?.colorValueField,
                            } }), _jsx(ColorPickerSwatch, { colors: swatchColors.slice(0, swatchColors.length / 2), onChange: (hsvColor) => updateColor(hsvColor), ...slotProps?.swatch }), _jsx(ColorPickerSwatch, { colors: swatchColors.slice(swatchColors.length / 2), onChange: (hsvColor) => updateColor(hsvColor), ...slotProps?.swatch }), !hideApplyButton && (_jsx(ApplyButton, { variant: "outlined", fullWidth: true, ...slotProps?.applyButton, onClick: (e) => {
                                onApply?.(color);
                                slotProps?.applyButton?.onClick?.(e);
                            }, children: applyButtonText })), bottomNode] })] })) }));
};
//# sourceMappingURL=ColorPicker.js.map